import { useEffect, useState } from "react";
import {
  DataRoomContext,
  DataRoomFolderQuery,
  DataRoomPermission,
  useDataRoomFastQuery,
  useDataRoomFolderQuery,
  useUpdateDataRoomFolderMutation,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { AnimatedModal } from "../AnimatedModal";
import { CloseIcon } from "../CloseIcon";
import { H3 } from "../Heading";
import Loading from "../Loading";
import { TextInput } from "../tailwind/TextInput";
import { Button } from "../tailwind/Button";
import { useParams } from "react-router-dom";
import Dropdown, { Option } from "../tailwind/Dropdown";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";

interface Props {
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
  dataRoomFolderId: string;
}

export function EditFolder(props: Props) {
  const client = useGqlClient();

  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const [folderId, setFolderId] = useState(props.dataRoomFolderId);
  useEffect(() => {
    if (props.dataRoomFolderId) {
      setFolderId(props.dataRoomFolderId);
    }
  }, [props.dataRoomFolderId]);

  const { data, isLoading, error } = useDataRoomFolderQuery(
    client,
    {
      id: folderId,
    },
    {
      enabled: props.open && folderId !== "",
    },
  );

  const dataRoomQuery = useDataRoomFastQuery(
    client,
    {
      dealId: activeDealId ? activeDealId : "",
    },
    {
      enabled: props.open && !!activeDealId,
    },
  );

  return (
    <AnimatedModal open={props.open} onClose={props.onClose}>
      <EditFolderHeader
        onClose={props.onClose}
        data={data}
        isLoading={isLoading || dataRoomQuery.isPending}
        error={error || dataRoomQuery.error}
      />
      <EditFolderContent
        data={data}
        isLoading={isLoading || dataRoomQuery.isPending}
        error={error || dataRoomQuery.error}
        onClose={props.onClose}
        onUpdated={props.onUpdated}
        permissions={
          dataRoomQuery.data ? dataRoomQuery.data.deal.dataRoom.permissions : []
        }
      />
    </AnimatedModal>
  );
}

interface EditFolderProps {
  isLoading: boolean;
  error: any;
  data: DataRoomFolderQuery | undefined;
  onClose: () => void;
}

function EditFolderHeader(props: EditFolderProps) {
  let title = "Edit Folder";

  if (props.error) {
    title = "Error";
  }

  if (props.data) {
    title = `Edit ${props.data.dataRoomFolder.name}`;
  }

  return (
    <div className="flex items-center justify-between">
      <H3>{title}</H3>
      <CloseIcon onClose={props.onClose} />
    </div>
  );
}

function EditFolderContent(
  props: EditFolderProps & {
    onUpdated?: () => void;
    permissions: Pick<DataRoomPermission, "id" | "name" | "description">[];
  },
) {
  const client = useGqlClient();
  const { invalidateDataRoomFolder } = useInvalidateQueryKeys();

  const [folderName, setName] = useState("");

  const updateDataRoomFolder = useUpdateDataRoomFolderMutation(client);
  const [permission, setPermission] = useState<Option>({
    label: "Loading...",
    description: "...",
    value: "",
  });

  useEffect(() => {
    if (props.data) {
      setName(props.data.dataRoomFolder.name);
      setPermission({
        value: props.data.dataRoomFolder.permissions[0].id,
        label: props.data.dataRoomFolder.permissions[0].name,
        description: props.data.dataRoomFolder.permissions[0].description,
      });
    }
  }, [props.data, props.isLoading, props.error]);

  if (props.error) {
    return (
      <div className="flex justify-center items-center p-4">
        <p className="text-gray-700 font-bold">Something went wrong</p>
      </div>
    );
  }

  if (props.isLoading || !props.data) {
    return <Loading />;
  }

  return (
    <div>
      <form
        onSubmit={(e) => {
          e.preventDefault();

          if (!props.data) {
            return;
          }

          updateDataRoomFolder.mutate(
            {
              input: {
                id: props.data.dataRoomFolder.id,
                name: folderName,
                permissionIDs: permission.value ? [permission.value] : [],
              },
            },
            {
              onSuccess: () => {
                if (props.onUpdated) {
                  props.onUpdated();
                }

                if (props.data?.dataRoomFolder.id) {
                  invalidateDataRoomFolder(props.data.dataRoomFolder.id);
                }

                props.data?.dataRoomFolder.parentFolders?.forEach((parent) => {
                  invalidateDataRoomFolder(parent.id);
                });

                props.onClose();
              },
            },
          );
        }}
      >
        <TextInput
          margin="m 0 0 0"
          ignore1p
          label="Name"
          placeholder="Name of the file"
          value={folderName}
          onChange={(e) => setName(e.currentTarget.value)}
        />

        {props.data.dataRoomFolder.context === DataRoomContext.Deal ? (
          <div className="mt-3">
            <Dropdown
              label="Permission level"
              defaultOption={{
                label: props.permissions[0].name,
                description: props.permissions[0].description,
                value: props.permissions[0].id,
              }}
              selectedOption={permission ?? undefined}
              onSelect={(o) => {
                setPermission(o);
              }}
              options={props.permissions.map((p) => {
                return {
                  value: p.id,
                  label: p.name,
                  description: p.description,
                };
              })}
            />
          </div>
        ) : null}

        <div className="mt-12 flex justify-end">
          <Button
            type="submit"
            text="Save"
            variant="positive"
            isLoading={updateDataRoomFolder.isPending}
            isDisabled={folderName === ""}
            loadingText="Saving..."
          />
        </div>
      </form>
    </div>
  );
}
